// Save / load. The entire GameState is plain data (cards and skills are
// referenced by id, never by function), so it round-trips through JSON without
// special handling. Loading is defensive: a corrupt or partial save is merged
// onto a fresh default so a schema change never bricks an existing player.

import { SAVE_KEY, VERSION } from '@data/version';
import { BALANCE } from '@data/balance';
import { CARD_BY_ID } from '@data/cards';
import { createInitialState, refreshDerived, type GameState } from './state';

/** Merges a parsed save blob onto fresh defaults, section by section, so any
 *  field added since the save was written gets a sane value. */
function migrate(data: unknown): GameState {
  const base = createInitialState();
  if (!data || typeof data !== 'object') return base;
  const d = data as Record<string, unknown>;
  const run = (d.run ?? {}) as Record<string, unknown>;
  const meta = (d.meta ?? {}) as Record<string, unknown>;
  const pending = (d.pending ?? {}) as Record<string, unknown>;
  const settings = (d.settings ?? {}) as Record<string, unknown>;

  const state: GameState = {
    ...base,
    version: typeof d.version === 'string' ? d.version : VERSION,
    gameTime: typeof d.gameTime === 'number' ? d.gameTime : 0,
    lastSavedWall: typeof d.lastSavedWall === 'number' ? d.lastSavedWall : Date.now(),
    run: {
      ...base.run,
      ...run,
      resources: { ...base.run.resources, ...(run.resources as object) },
      buildings: { ...base.run.buildings, ...(run.buildings as object) },
      modifiers: { ...base.run.modifiers, ...(run.modifiers as object) },
      nodes: Array.isArray(run.nodes) ? (run.nodes as GameState['run']['nodes']) : base.run.nodes,
      pickedCards: Array.isArray(run.pickedCards) ? (run.pickedCards as string[]) : [],
      techs:
        run.techs && typeof run.techs === 'object'
          ? (run.techs as Record<string, number>)
          : {},
      artifacts: Array.isArray(run.artifacts)
        ? (run.artifacts as GameState['run']['artifacts'])
        : [],
      buffs: Array.isArray(run.buffs) ? (run.buffs as GameState['run']['buffs']) : [],
      conquered: typeof run.conquered === 'boolean' ? run.conquered : false,
      // Clamp the visual-tier index so a corrupt save can never index past the
      // settlementTiers table (the render loop reads tiers[settlementTier].name).
      settlementTier: Math.min(
        Math.max(0, Math.floor(Number(run.settlementTier) || 0)),
        BALANCE.settlementTiers.length - 1,
      ),
    },
    meta: {
      ...base.meta,
      ...meta,
      skillLevels: { ...((meta.skillLevels as object) ?? {}) } as Record<string, number>,
      planet: Math.max(0, Math.floor(Number(meta.planet) || 0)),
      planetsConquered: Math.max(0, Math.floor(Number(meta.planetsConquered) || 0)),
      cosmic: Math.max(0, Number(meta.cosmic) || 0),
    },
    pending: {
      ...base.pending,
      ...pending,
      // Sanitize card ids against the current pool: a save holding removed or
      // corrupt ids would otherwise load sim-paused on an empty card overlay
      // with no way out. Nulling stale choices self-heals: gatePending survives
      // migration so the gate boss is re-summoned (or, pre-gate, checkGate
      // re-opens the free card since progress still exceeds the threshold).
      cardChoices: (() => {
        const raw = Array.isArray(pending.cardChoices)
          ? (pending.cardChoices as unknown[]).filter(
              (id): id is string => typeof id === 'string' && !!CARD_BY_ID[id],
            )
          : null;
        return raw && raw.length ? raw : null;
      })(),
    },
    settings: { ...base.settings, ...settings },
  };
  return state;
}

export function saveGame(state: GameState): void {
  try {
    state.version = VERSION;
    state.lastSavedWall = Date.now();
    localStorage.setItem(SAVE_KEY, JSON.stringify(state));
  } catch (err) {
    console.error('[save] could not persist game:', err);
  }
}

export function loadGame(): GameState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;
    const state = migrate(JSON.parse(raw));
    refreshDerived(state);
    return state;
  } catch (err) {
    console.error('[load] save was unreadable, starting fresh:', err);
    return null;
  }
}

/** Serializes the live state to a blob for cloud save. */
export function serializeSave(state: GameState): string {
  return JSON.stringify(state);
}

/** Parses a cloud-save blob into a usable state (migrated + derived), or null. */
export function parseSaveBlob(blob: string): GameState | null {
  try {
    const state = migrate(JSON.parse(blob));
    refreshDerived(state);
    return state;
  } catch (err) {
    console.error('[save] could not parse cloud save:', err);
    return null;
  }
}

export function hasSave(): boolean {
  try {
    return localStorage.getItem(SAVE_KEY) != null;
  } catch {
    return false;
  }
}

export function clearSave(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch (err) {
    console.error('[save] could not clear save:', err);
  }
}
